import React, {
  useState,
  useRef,
  useCallback,
} from "react";
import { Link } from "react-router-dom";
import { veagroupLogo } from "../data/home/home";

const Footer: React.FC = () => {
  const [email, setEmail] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const [toastColor, setToastColor] =
    useState("bg-[#FE6019]");
  const [progress, setProgress] = useState(100);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toastTimeout = useRef<NodeJS.Timeout | null>(null);
  const progressInterval = useRef<NodeJS.Timeout | null>(
    null
  );

  const handleCloseToast = () => {
    setShowToast(false);
    setProgress(100);
    if (toastTimeout.current)
      clearTimeout(toastTimeout.current);
    if (progressInterval.current)
      clearInterval(progressInterval.current);
  };

  const handleSubscribe = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      console.log("Subscribe triggered:", {
        isSubmitting,
        email,
      });
      if (isSubmitting) return;
      if (!email) {
        setToastMsg("Please enter an email.");
        setToastColor("bg-red-500");
        setShowToast(true);
        return;
      }
      setIsSubmitting(true);
      setToastMsg("Subscribing...");
      setToastColor("bg-gray-500");
      setShowToast(true);

      try {
        const response = await fetch("/api/subscribe", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        });
        const data = await response.json();
        console.log("Response (Newsletter):", {
          status: response.status,
          data,
        });
        if (response.ok) {
          setToastMsg(
            data.message || "Subscribed successfully!"
          );
          setToastColor("bg-[#FE6019]");
          setEmail("");
        } else {
          setToastMsg(data.error || "Error subscribing.");
          setToastColor("bg-red-500");
        }
      } catch (error) {
        console.log("Fetch error (Newsletter):", error);
        setToastMsg("Failed to connect to server.");
        setToastColor("bg-red-500");
      } finally {
        setIsSubmitting(false);
      }

      setProgress(100);
      let elapsed = 0;
      const duration = 4000;
      const interval = 40;
      if (progressInterval.current)
        clearInterval(progressInterval.current);
      progressInterval.current = setInterval(() => {
        elapsed += interval;
        setProgress(
          Math.max(0, 100 - (elapsed / duration) * 100)
        );
      }, interval);
      if (toastTimeout.current)
        clearTimeout(toastTimeout.current);
      toastTimeout.current = setTimeout(() => {
        setShowToast(false);
        setProgress(100);
        if (progressInterval.current)
          clearInterval(progressInterval.current);
      }, duration);
    },
    [email]
  );

  return (
    <footer className="bg-black dark:bg-white text-white dark:text-black pt-12 pb-6 text-sm">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-9 gap-y-8 gap-x-12 justify-between text-left">
          <div className="text-left lg:col-span-3">
            <div className="flex items-center mb-5">
              <Link to="/">
                <img
                  src={veagroupLogo}
                  alt="VEA Group Logo"
                  className="h-5 w-auto"
                />
              </Link>
            </div>
            <div className="mb-2 font-semibold text-white dark:text-black mt-4">
              Headquarters in Viet Nam:
            </div>
            <div className="flex items-start text-gray-300 dark:text-gray-700 text-xs mb-1">
              <svg
                className="w-4 h-4 text-[#FE6019] mr-2 mt-0.5"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
              </svg>
              <span>
                115 Street No. 11, Thu Duc Ward, Ho Chi Minh
                City
              </span>
            </div>
            <div className="flex items-start text-gray-300 dark:text-gray-700 text-xs mb-1">
              <svg
                className="w-5 h-5 text-[#FE6019] mr-2 mt-0.5"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
              </svg>
              <span>
                HSC Building, 162B Dien Bien Phu, Xuan Hoa
                Ward, Ho Chi Minh City
              </span>
            </div>
            <div className="mb-2 font-semibold text-white dark:text-black mt-4">
              Headquarters in the United States:
            </div>
            <div className="flex items-start text-gray-300 dark:text-gray-700 text-xs mb-1">
              <svg
                className="w-4 h-4 text-[#FE6019] mr-2 mt-0.5"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
              </svg>
              <span>
                5900 Balcones Drive STE 100, Austin, TX
                78731
              </span>
            </div>
            <div className=" mt-5 flex items-center mb-2 text-gray-300 dark:text-gray-700">
              <svg
                className="w-5 h-5 text-[#FE6019] mr-2"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M20 4H4a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 002-2V6a2 2 0 00-2-2zm0 2v.01L12 13 4 6.01V6h16zM4 20V8.99l8 7 8-7V20H4z" />
              </svg>
              <span>hr@veagroup.global</span>
            </div>
            <div className="flex items-center mb-2 text-gray-300 dark:text-gray-700">
              <svg
                className="w-5 h-5 text-[#FE6019] mr-2"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M20 4H4a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 002-2V6a2 2 0 00-2-2zm0 2v.01L12 13 4 6.01V6h16zM4 20V8.99l8 7 8-7V20H4z" />
              </svg>
              <span>phongnguyen@veagroup.global</span>
            </div>
          </div>
          <div className="text-left lg:col-span-2">
            <h3 className="font-primary text-white dark:text-black font-bold text-lg md:text-xl mb-4 not-italic">
              Useful Links
            </h3>
            <ul className="space-y-2 mb-4 text-gray-300 dark:text-gray-700">
              <li>
                <Link
                  to="/career"
                  className="hover:text-[#FE6019] transition-colors duration-200"
                >
                  Career
                </Link>
              </li>
              <li>
                <Link
                  to="/cooperation"
                  className="hover:text-[#FE6019] transition-colors duration-200"
                >
                  Cooperation
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy-policy"
                  className="hover:text-[#FE6019] transition-colors duration-200"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/terms-of-service"
                  className="hover:text-[#FE6019] transition-colors duration-200"
                >
                  Terms of Service
                </Link>
              </li>
            </ul>
            <div className="flex space-x-4">
              <a
                href="/"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-[#FE6019] hover:bg-[#e55617] text-white text-xl transition-colors duration-200"
              >
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M22.675 0h-21.35C.595 0 0 .592 0 1.326v21.348C0 23.408.595 24 1.325 24h11.495v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.797.143v3.24l-1.918.001c-1.504 0-1.797.715-1.797 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116C23.406 24 24 23.408 24 22.674V1.326C24 .592 23.406 0 22.675 0" />
                </svg>
              </a>
              <a
                href="/"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-[#FE6019] hover:bg-[#e55617] text-white text-xl transition-colors duration-200"
              >
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11.5 19h-3v-9h3v9zm-1.5-10.268c-.966 0-1.75-.784-1.75-1.75s.784-1.75 1.75-1.75 1.75.784 1.75 1.75-.784 1.75-1.75 1.75zm13.5 10.268h-3v-4.604c0-1.096-.021-2.507-1.528-2.507-1.529 0-1.764 1.195-1.764 2.427v4.684h-3v-9h2.881v1.233h.041c.401-.761 1.379-1.563 2.841-1.563 3.039 0 3.6 2.001 3.6 4.6v4.73z" />
                </svg>
              </a>
              <a
                href="/"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-[#FE6019] hover:bg-[#e55617] text-white text-xl transition-colors duration-200"
              >
                <svg
                  className="w-10 h-10"
                  viewBox="0 0 36 36"
                  fill="currentColor"
                >
                  <path
                    d="M23.625 11.625c-.525 0-.975-.45-.975-.975V9h-2.4v11.4c0 1.5-1.2 2.7-2.7 2.7s-2.7-1.2-2.7-2.7 1.2-2.7 2.7-2.7c.15 0 .3 0 .45.075v-2.475c-.15 0-.3-.075-.45-.075-2.85 0-5.175 2.325-5.175 5.175s2.325 5.175 5.175 5.175 5.175-2.325 5.175-5.175V16.5c.825.525 1.8.825 2.775.825v-2.4c-.525 0-.975-.075-1.425-.3z"
                    fill="white"
                  />
                </svg>
              </a>
            </div>
          </div>
          <div className="text-left lg:col-span-4">
            <h3 className="font-primary text-white dark:text-black font-bold text-lg md:text-xl mb-4 not-italic">
              Newsletter
            </h3>
            <p className="mb-3 text-gray-300 dark:text-gray-700">
              Subscribe to get the latest information and
              updates.
            </p>
            <form
              onSubmit={handleSubscribe}
              className="flex flex-col sm:flex-row gap-2"
            >
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Your email address"
                className="rounded-lg px-4 py-2 text-black dark:text-white bg-white dark:bg-black border border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-[#FE6019] flex-1 min-w-0"
              />
              <button
                type="submit"
                className="px-5 py-2 rounded-lg bg-[#FE6019] hover:bg-[#e55617] text-white font-semibold transition-colors duration-200"
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? "Subscribing..."
                  : "Subscribe"}
              </button>
            </form>
          </div>
        </div>
        {showToast && (
          <div
            className={`fixed top-6 right-6 z-50 min-w-[260px] max-w-xs ${toastColor} text-white font-primary font-semibold shadow-lg rounded-lg overflow-hidden animate-fade-in-out`}
          >
            <div className="flex items-center justify-between px-4 py-3">
              <span>{toastMsg}</span>
              <button
                onClick={handleCloseToast}
                className="ml-4 text-white hover:text-gray-200 text-lg font-bold focus:outline-none"
              >
                ×
              </button>
            </div>
            <div className="h-1 bg-opacity-30 bg-white">
              <div
                style={{ width: `${progress}%` }}
                className="h-1 bg-white transition-all duration-100"
              ></div>
            </div>
          </div>
        )}
        <style>{`
          @keyframes fade-in-out {
            0% { opacity: 0; transform: translateY(-20px); }
            10% { opacity: 1; transform: translateY(0); }
            90% { opacity: 1; transform: translateY(0); }
            100% { opacity: 0; transform: translateY(-10px); }
          }
          .animate-fade-in-out {
            animation: fade-in-out 4s;
          }
        `}</style>
        <div className="text-center text-gray-400 dark:text-gray-700 text-xs mt-8">
          VEA Group © 2025. All Rights Reserved.
        </div>
      </div>
    </footer>
  );
};

export default Footer;
